import {
    ButtonGroup,
    ClickAwayListener,
    IconButton,
    Paper,
    Popper,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { ChangeEvent, KeyboardEvent, useRef, useState } from "react";

import AddBoxIcon from "@mui/icons-material/AddBox";
import { CallOffCodeChips } from "../call-off-code-chips";
import CalloffCodeGenerator from "../calloff-code-generator/CalloffCodeGenerator";
import localisation from "domains/partitions/localisation";
import localisations from "features/localisation";

interface IProps {
    callOffCodes?: string[];
    onCallOffCodesChanged?: (callOffCodes: string[]) => void;
}

const CreateCallOffCode = (props: IProps) => {
    const { callOffCodes: defaultCallOffCodes, onCallOffCodesChanged } = props;

    const {
        calloffCodeLabel,
        generateCalloffButtonLabel,
        calloffCodeHelperText,
        addCalloffButtonLabel,
        generatedCallOffCodeButtonHelperText,
        addCallOffCodeButtonHelperText,
        calloffCodesTitle,
        emptyCallOffCodeTitle,
    } = localisation;

    const addCallOffCodeButtonRef = useRef<any>();

    const [callOffCodeNotAddedWarningAnchor, setCallOffCodeNotAddedWarningAnchor] = useState(null);

    const [callOffCodeValue, setCallOffCodeValue] = useState("");

    const [errorText, setErrorText] = useState("");

    const inputRef = useRef<any>();

    const openCallOffCodeNotAddedWarning = Boolean(callOffCodeNotAddedWarningAnchor);
    const callOffCodeNotAddedWarningId = openCallOffCodeNotAddedWarning ? "call-off-code-not-added-warning" : undefined;

    const handleCodeGenerated = (newCalOffCode: string) => {
        setCallOffCodeValue(newCalOffCode);
        inputRef?.current?.focus();
    };

    const handleTextBoxChange = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setCallOffCodeValue(event.target.value);
    };

    const handleEnterKeyPressed = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.code == "Enter") {
            validateAndAddCallOffCode();
        }
    };

    const handleAddCallOffCodeClick = () => {
        validateAndAddCallOffCode();
    };

    const validateAndAddCallOffCode = () => {
        setCallOffCodeNotAddedWarningAnchor(null);

        if (callOffCodeValue?.trim().length === 0) {
            setErrorText(localisations.createCallOffCode.clientErrors.callOffCodeRequired);
            return;
        }

        if (callOffCodeValue?.trim().length >= 50) {
            setErrorText(localisations.createCallOffCode.clientErrors.callOffCodeTooLong);
            return;
        }

        const callOffCodes: string[] = [...(defaultCallOffCodes ?? []), callOffCodeValue.trim()];
        onCallOffCodesChanged?.(callOffCodes);
        setCallOffCodeValue("");
        setErrorText("");
    };

    const handleDeleteCallOffCodeClick = (callOffCodes: string[]) => {
        onCallOffCodesChanged?.(callOffCodes);
    };

    const handleShouldDisplayErrors = (): boolean | undefined => {
        return errorText.length > 0;
    };

    const handleClickAway = () => {
        if (errorText.length !== 0) {
            setErrorText("");
        }

        if (callOffCodeValue.trim().length === 0) {
            setCallOffCodeNotAddedWarningAnchor(null);
        }

        if (callOffCodeValue.trim().length > 0) {
            setCallOffCodeNotAddedWarningAnchor(addCallOffCodeButtonRef.current);
        }
    };

    return (
        <>
            <ClickAwayListener onClickAway={handleClickAway}>
                <TextField
                    error={handleShouldDisplayErrors()}
                    id="calloffCode-field"
                    label={calloffCodeLabel}
                    inputProps={{
                        "aria-label": calloffCodeLabel,
                    }}
                    onFocus={() => {
                        setErrorText("");
                        setCallOffCodeNotAddedWarningAnchor(null);
                    }}
                    style={{ margin: 8 }}
                    autoComplete="none"
                    helperText={errorText ? errorText : calloffCodeHelperText}
                    inputRef={inputRef}
                    onKeyDown={handleEnterKeyPressed}
                    fullWidth
                    InputProps={{
                        endAdornment: (
                            <ButtonGroup disableElevation>
                                <CalloffCodeGenerator
                                    id="calloffCode-generator"
                                    helperText={generatedCallOffCodeButtonHelperText}
                                    label={generateCalloffButtonLabel}
                                    onGenerated={handleCodeGenerated}
                                />
                                <Tooltip title={addCallOffCodeButtonHelperText}>
                                    <IconButton
                                        id="add-call-off-code-button"
                                        ref={addCallOffCodeButtonRef}
                                        color="primary"
                                        onClick={handleAddCallOffCodeClick}
                                        aria-label={addCalloffButtonLabel}
                                    >
                                        <AddBoxIcon />
                                    </IconButton>
                                </Tooltip>
                            </ButtonGroup>
                        ),
                    }}
                    margin="normal"
                    value={callOffCodeValue}
                    onChange={handleTextBoxChange}
                />
            </ClickAwayListener>
            {
                <CallOffCodeChips
                    style={{ marginLeft: 10, marginBottom: 10 }}
                    title={calloffCodesTitle}
                    emptyCallOffCodeTitle={emptyCallOffCodeTitle}
                    callOffCodes={defaultCallOffCodes ?? []}
                    deletable
                    onDeleted={handleDeleteCallOffCodeClick}
                ></CallOffCodeChips>
            }
            <Popper
                id={callOffCodeNotAddedWarningId}
                open={openCallOffCodeNotAddedWarning}
                anchorEl={callOffCodeNotAddedWarningAnchor}
            >
                <Paper elevation={5} sx={{ p: 2 }}>
                    <Typography>{localisations.createCallOffCode.callOffCodeNotAddedWarning}</Typography>
                </Paper>
            </Popper>
        </>
    );
};

export default CreateCallOffCode;
